import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  Skeleton,
  toast,
} from '@eon/ui';
import { resolveApiError, translateStationMetaStatus, translateStationType, useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import { formatDateTime } from '@eon/utils';
import { AddStationDeviceDialog } from '@/components/AddStationDeviceDialog';
import { DeviceStatusBadge } from '@/components/status/StatusBadges';
import { ArrowLeft, Pencil, Plus, Trash2, Unlink } from 'lucide-react';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

export function StationDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stationId } = useParams({ from: '/_app/stations/$stationId' });
  const { data: station, isLoading } = hooks.useAdminStation(stationId);
  const del = hooks.useDeleteStation();
  const unlink = hooks.useRemoveStationDevice();
  const [addOpen, setAddOpen] = useState(false);

  const meta = station?.metadata ?? {};
  const contact = (station?.contact ?? {}) as Record<string, unknown>;
  const hasContact =
    (typeof contact.email === 'string' && contact.email) ||
    (typeof contact.phone === 'string' && contact.phone) ||
    (typeof contact.website === 'string' && contact.website);
  const hours = station?.operatingHours ?? {};
  const stationType = typeof meta.type === 'string' ? meta.type : null;
  const stationStatus = typeof meta.status === 'string' ? meta.status : null;
  const capacity = typeof meta.capacity === 'number' ? meta.capacity : null;
  const installationDate =
    typeof meta.installationDate === 'string' ? meta.installationDate : null;
  const displayOnMap =
    typeof meta.displayOnMap === 'boolean' ? meta.displayOnMap : station?.isPublic;

  const onUnlinkDevice = (deviceId: string, label: string) => {
    if (!station || !window.confirm(label)) return;
    unlink.mutate(
      { stationId: station.id, deviceId },
      {
        onSuccess: () => toast.success(t('station.link.unlinked')),
        onError: (e) => toast.error(resolveApiError(e, t)),
      },
    );
  };

  const onDelete = () => {
    if (!station || !window.confirm(`"${station.name}"`)) return;
    del.mutate(station.id, {
      onSuccess: () => {
        toast.success(t('actions.delete'));
        navigate({ to: '/stations' });
      },
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title={t('nav.stations')} />
        <Skeleton className="h-64 w-full" />
      </>
    );
  }

  if (!station) {
    return (
      <>
        <PageHeader title={t('station.notFound')} />
        <Button variant="outline" asChild>
          <Link to="/stations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('station.create.back')}
          </Link>
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={station.name}
        description={station.address ?? undefined}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/stations">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('station.create.back')}
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/stations/$stationId/edit" params={{ stationId: station.id }}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('actions.edit')}
              </Link>
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={del.isPending}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('actions.delete')}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('station.detail.basic')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label={t('station.name')} value={station.name} />
            <InfoRow
              label={t('station.detail.type')}
              value={stationType ? <Badge variant="secondary">{translateStationType(t, stationType)}</Badge> : '—'}
            />
            <InfoRow
              label={t('station.status')}
              value={
                <Badge variant={station.active ? 'success' : 'secondary'}>
                  {stationStatus
                    ? translateStationMetaStatus(t, stationStatus)
                    : station.active
                      ? t('station.detail.statusActive')
                      : t('station.detail.statusInactive')}
                </Badge>
              }
            />
            <InfoRow
              label={t('station.detail.availability')}
              value={station.active ? t('common.yes') : t('common.no')}
            />
            <InfoRow label={t('station.devices')} value={station.deviceCount} />
            {capacity != null ? (
              <InfoRow label={t('station.detail.capacity')} value={capacity} />
            ) : null}
            {installationDate ? (
              <InfoRow label={t('station.detail.installationDate')} value={installationDate} />
            ) : null}
            {station.description ? (
              <InfoRow label={t('station.create.descriptionField')} value={station.description} />
            ) : null}
            {station.organizationId ? (
              <InfoRow
                label={t('station.detail.organization')}
                value={<span className="font-mono text-xs">{station.organizationId}</span>}
              />
            ) : null}
            {station.createdAt ? (
              <InfoRow label={t('user.createdAt')} value={formatDateTime(station.createdAt)} />
            ) : null}
            {station.updatedAt ? (
              <InfoRow label={t('station.detail.updatedAt')} value={formatDateTime(station.updatedAt)} />
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('station.detail.location')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow label={t('station.address')} value={station.address} />
            {station.city ? <InfoRow label={t('station.detail.city')} value={station.city} /> : null}
            {station.country ? (
              <InfoRow label={t('station.detail.country')} value={station.country} />
            ) : null}
            {station.latitude != null && station.longitude != null ? (
              <InfoRow
                label={t('station.detail.coordinates')}
                value={`${station.latitude.toFixed(6)}, ${station.longitude.toFixed(6)}`}
              />
            ) : null}
            <InfoRow
              label={t('station.detail.displayOnMap')}
              value={displayOnMap ? t('common.yes') : t('common.no')}
            />
          </CardContent>
        </Card>

        {hasContact ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('station.detail.contact')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {contact.email ? (
                <InfoRow label={t('user.email')} value={String(contact.email)} />
              ) : null}
              {contact.phone ? (
                <InfoRow label={t('station.detail.phone')} value={String(contact.phone)} />
              ) : null}
              {contact.website ? (
                <InfoRow label={t('station.detail.website')} value={String(contact.website)} />
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {Object.keys(hours).length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('station.detail.operatingHours')}</CardTitle>
            </CardHeader>
            <CardContent>
              <InfoRow
                label={t('station.detail.is24Hours')}
                value={hours.is24Hours ? t('common.yes') : t('common.no')}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base">{t('station.detail.connectedDevices')}</CardTitle>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('station.link.add')}
            </Button>
          </CardHeader>
          <CardContent>
            {station.devices && station.devices.length > 0 ? (
              <ul className="divide-border divide-y">
                {station.devices.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 py-2">
                    <Link
                      to="/devices/$deviceId"
                      params={{ deviceId: d.id }}
                      className="min-w-0 flex-1 hover:underline"
                    >
                      <span className="font-medium">{d.displayName?.trim() || d.serialNumber}</span>
                      <span className="text-muted-foreground ml-2 font-mono text-xs">{d.serialNumber}</span>
                    </Link>
                    <div className="flex shrink-0 items-center gap-2">
                      {d.status ? <DeviceStatusBadge status={d.status} /> : null}
                      <Button
                        variant="ghost"
                        size="icon"
                        title={t('station.link.unlink')}
                        onClick={() =>
                          onUnlinkDevice(
                            d.id,
                            `${t('station.link.unlinkConfirm')} "${d.displayName?.trim() || d.serialNumber}"`,
                          )
                        }
                        disabled={unlink.isPending}
                      >
                        <Unlink className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">{t('station.detail.noDevices')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <AddStationDeviceDialog station={station} open={addOpen} onOpenChange={setAddOpen} />
    </>
  );
}
